import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'dentacares-install-dismissed-until';
const DISMISS_DURATION = 1000 * 60 * 60 * 24 * 7; // 7 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const isStandaloneMode = () => {
  if (typeof window === 'undefined') return true;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean })?.standalone
  );
};

const isDismissed = () => {
  if (typeof window === 'undefined') return true;
  const stored = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return stored > Date.now();
};

const rememberDismissal = () => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION));
};

export function usePWAInstallPrompt() {
  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      if (isStandaloneMode() || isDismissed()) return;
      setPromptEvent(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    const handleInstalled = () => {
      setPromptEvent(null);
      setVisible(false);
      localStorage.removeItem(DISMISS_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setPromptEvent(null);
    setVisible(false);
    if (choice.outcome === 'dismissed') rememberDismissal();
  }, [promptEvent]);

  const dismissPrompt = useCallback(() => {
    rememberDismissal();
    setPromptEvent(null);
    setVisible(false);
  }, []);

  return {
    canInstall: visible && !!promptEvent,
    promptInstall,
    dismissPrompt,
  };
}
